/**
 * DayCell — Individual day cell in the CalendarGrid.
 *
 * Renders the day number, colored dots for calendar events (max 3),
 * and small checkbox icons for tasks due on that day.
 * Visually differentiates tasks (pink checkbox) from events (colored circles).
 *
 * @see Requirements 5.1, 5.2, 5.3, 5.8, 10.5
 */

import type { CalendarEvent } from '../../types/calendar';
import type { Task } from '../../types/tasks';

export interface DayCellProps {
  /** ISO date string (YYYY-MM-DD) for this cell. */
  date: string;
  /** Calendar events occurring on this day. */
  events: CalendarEvent[];
  /** Tasks with a due date on this day. */
  tasks: Task[];
  /** Whether this date is today. */
  isToday: boolean;
  /** Whether this date is the currently selected date. */
  isSelected: boolean;
  /** Whether this date belongs to an adjacent month (faded). */
  isOtherMonth: boolean;
  /** Click handler for date selection. */
  onClick: () => void;
}

/** Default event dot color when no color is specified. */
const DEFAULT_EVENT_COLOR = 'var(--color-secondary)'; // lavender from design system

/** Maximum number of event dots to display. */
const MAX_DOTS = 3;

/**
 * DayCell renders a single day in the monthly calendar grid.
 *
 * Visual differentiation (Req 5.8):
 * - Events: small colored circles (using event.color or default lavender)
 * - Tasks: small pink checkbox icon
 *
 * Accessibility:
 * - role="gridcell" with aria-selected and aria-label
 * - Descriptive label includes date and counts of events/tasks
 */
export function DayCell({
  date,
  events,
  tasks,
  isToday,
  isSelected,
  isOtherMonth,
  onClick,
}: DayCellProps) {
  const dayNumber = parseInt(date.split('-')[2] ?? '1', 10);
  const eventCount = events.length;
  const taskCount = tasks.length;

  // Build accessible label
  const dateObj = new Date(date + 'T00:00:00');
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const parts: string[] = [formattedDate];
  if (eventCount > 0) {
    parts.push(`${eventCount} event${eventCount !== 1 ? 's' : ''}`);
  }
  if (taskCount > 0) {
    parts.push(`${taskCount} task${taskCount !== 1 ? 's' : ''}`);
  }
  const ariaLabel = parts.join(', ');

  // Determine visible event dots (max 3)
  const visibleEvents = events.slice(0, MAX_DOTS);
  const hasMoreEvents = eventCount > MAX_DOTS;

  return (
    <button
      type="button"
      role="gridcell"
      aria-selected={isSelected}
      aria-label={ariaLabel}
      aria-current={isToday ? 'date' : undefined}
      onClick={onClick}
      className={[
        'relative flex flex-col items-center justify-center w-full aspect-square rounded-lg',
        'text-sm font-medium transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
        'hover:bg-hover-bg',
        // Selected state: filled primary background
        isSelected && !isToday
          ? 'bg-primary text-white shadow-card'
          : '',
        // Today state: ring indicator
        isToday && !isSelected
          ? 'ring-2 ring-primary bg-primary-light text-primary font-bold'
          : '',
        // Today + selected
        isToday && isSelected
          ? 'bg-primary text-white shadow-card ring-2 ring-primary-dark font-bold'
          : '',
        // Other month: faded
        isOtherMonth
          ? 'opacity-40'
          : '',
        // Default state
        !isSelected && !isToday
          ? 'text-text-primary'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Day number */}
      <span className="leading-none">{dayNumber}</span>

      {/* Indicators row */}
      {(eventCount > 0 || taskCount > 0) && (
        <div className="absolute bottom-1 flex items-center gap-0.5">
          {/* Event dots — colored circles */}
          {visibleEvents.map((event, idx) => (
            <span
              key={event.eventId || idx}
              className="w-1.5 h-1.5 rounded-full"
              style={{
                backgroundColor: isSelected
                  ? 'rgba(255,255,255,0.8)'
                  : event.color || DEFAULT_EVENT_COLOR,
              }}
              aria-hidden="true"
            />
          ))}
          {hasMoreEvents && (
            <span
              className={[
                'text-[8px] leading-none',
                isSelected ? 'text-white/80' : 'text-text-secondary',
              ].join(' ')}
              aria-hidden="true"
            >
              +
            </span>
          )}

          {/* Task indicators — small checkbox icon in pink */}
          {taskCount > 0 && (
            <TaskIndicator
              count={taskCount}
              isSelected={isSelected}
            />
          )}
        </div>
      )}
    </button>
  );
}

/** Small task checkbox icon indicator. */
function TaskIndicator({
  count,
  isSelected,
}: {
  count: number;
  isSelected: boolean;
}) {
  return (
    <span className="flex items-center gap-px" aria-hidden="true">
      <svg
        width="8"
        height="8"
        viewBox="0 0 16 16"
        fill="none"
        className={isSelected ? 'text-white/80' : 'text-primary'}
      >
        <rect
          x="1"
          y="1"
          width="14"
          height="14"
          rx="3"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M4.5 8L7 10.5L11.5 5.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {count > 1 && (
        <span
          className={[
            'text-[7px] leading-none font-medium',
            isSelected ? 'text-white/80' : 'text-primary',
          ].join(' ')}
        >
          {count}
        </span>
      )}
    </span>
  );
}
